// One-line summaries for raw events in the Events feed (runner detail has
// richer lines that know the runner's plans: screens/runnerActivity.ts).

import type { AdminEvent } from "@paceroo/domain/views/admin";
import { FORMAT_LABEL, methodLabel } from "./format";

const ms = (n: number | null) => (n ? (n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`) : "");
const join = (...parts: Array<string | undefined | null | false>) => parts.filter(Boolean).join(" · ");

export function eventSummary(e: AdminEvent): string {
  const f = e.fields;
  switch (e.event) {
    case "auth.sign_up":
      return `Signed up with ${methodLabel(f["method"] ?? "password")}`;
    case "auth.account_linked":
      return `Linked ${methodLabel(f["method"] ?? "")}`;
    case "auth.sign_in":
      return "Signed in";
    case "auth.email_verified":
      return "Verified email";
    case "plan.first_started":
      return "First plan";
    case "plan.archived":
      return "Archived a plan";
    case "plan.restored":
      return "Restored a plan";
    case "plan.deleted":
      return "Deleted a plan";
    case "auth.sign_in_failed":
      return `Sign-in refused (${f["status"] ?? "?"})`;
    case "auth.rate_limited":
      return `Rate-limited · ${f["action"] ?? "auth"}`;
    case "plan.started":
      return join(`${f["source"] ?? ""} plan`, f["weeks"] && `${f["weeks"]} wk`, f["methodology"], f["via"] === "mcp" && "via assistant");
    case "plan.edited":
      return join((f["kind"] ?? "change").replace(/_/g, " "), f["via"] === "mcp" && "via assistant");
    case "export.downloaded":
      return join(FORMAT_LABEL[f["format"] ?? ""] ?? f["format"], f["subject"]?.replace(/_/g, " "), f["via"] === "assistant_link" ? "signed link" : "in app");
    case "mcp.call":
      return join(f["tool"], f["outcome"], ms(e.durationMs));
    case "mcp.connected":
      return join(f["clientName"] || "an assistant", f["access"] === "read_write" ? "read and write" : "read only");
    case "mcp.disconnected":
      return join(f["clientId"], f["via"]);
    case "mcp.rate_limited":
      // limit / limiter arrive with the binding limiter; older events lack them.
      return join("Rate-limited", f["clientId"], f["limit"] && `limit ${f["limit"]}`, f["limiter"] === "memory" ? "fallback limiter" : undefined);
    case "api.error":
      return f["route"] ?? "";
    default:
      return Object.entries(f)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" · ");
  }
}

/** Failures render in the alert colour. */
export function isBadEvent(e: AdminEvent): boolean {
  return e.event === "api.error" || e.event === "auth.sign_in_failed" || e.fields["outcome"] === "error" || e.fields["outcome"] === "exception";
}
